import { type FieldErrors, type FieldValues, type FieldError } from "react-hook-form"

// This matches the existing Fluent Validation summary shape
export type ValidationSummaryData = Record<string, string[]>

/**
 * Transforms Zod/React Hook Form errors into a flat Record<string, string[]>
 */
export function formatRHFErrors<T extends FieldValues>(errors: FieldErrors<T>): ValidationSummaryData {
  const summary: ValidationSummaryData = {}

  for (const [key, error] of Object.entries(errors)) {
    if (error == null) continue

    // 1. Handle Top-level or Array-level errors (.message)
    if (isFieldError(error)) {
      summary[key] = [error.message!]
      continue
    }

    // 2. Handle Root errors (.root)
    if ("root" in error && isFieldError(error.root)) {
      summary[key] = [error.root.message!]
      continue
    }

    // 3. Handle Nested Errors (Array Items: properties.0, properties.1)
    if (typeof error === "object") {
      // We cast to [string, unknown][] to get rid of 'any' from Object.entries
      const entries = Object.entries(error) as Array<[string, unknown]>

      entries.forEach(([subKey, subError]) => {
        if (isFieldError(subError)) {
          // Format the key as "properties.1"
          summary[`${key}.${subKey}`] = [subError.message!]
        }
      })
    }
  }
  return summary
}

export type FluentValidationError = {
  propertyName: string
  errorMessage: string
  // ... other properties we don't need
}

export function formatFluentValidationErrors(errors: FluentValidationError[]): Record<string, string[]> {
  return errors.reduce(
    (acc, error) => {
      // If we haven't seen this property yet, initialize an array
      acc[error.propertyName] ??= []

      // Push the error message to the array for that property
      acc[error.propertyName].push(error.errorMessage)
      return acc
    },
    {} as Record<string, string[]>,
  )
}

function isFieldError(error: unknown): error is FieldError {
  return (
    error !== null &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as FieldError).message === "string"
  )
}
